import * as XLSX from 'xlsx';
import {
  resolveSiteDisplayName,
  resolveDrugOutColumns,
  resolveLabGroups,
  buildWaterDailySkeletonRows,
  buildWaterSettleSkeletonRows,
} from './officialExportTemplates.js';

function pad2(n) {
  return `${Number(n)}`.padStart(2, '0');
}

function isApproved(sub) {
  return String(sub?.status || '').trim() === '已通过';
}

function approvedOnly(list) {
  return (list || []).filter(isApproved);
}

function fillYmd(fillTime, createdAt) {
  const raw =
    fillTime != null && `${fillTime}`.trim() !== ''
      ? String(fillTime).trim()
      : String(createdAt || '').trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const slash = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
  if (slash) return `${slash[1]}-${pad2(slash[2])}-${pad2(slash[3])}`;
  const d = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function todayYmd() {
  const t = new Date();
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
}

function parseYm(ym) {
  const [yyStr, mmStr] = String(ym || '').split('-');
  const yy = Number(yyStr);
  const mm = Number(mmStr);
  const daysInMonth = new Date(yy, mm, 0).getDate();
  return {
    yyStr,
    mmStr,
    yy,
    mm,
    daysInMonth,
    monthTitle: `${yyStr}年${Number(mmStr)}月`,
  };
}

function isFutureDay(ymdKey, viewYm) {
  const today = todayYmd();
  if (String(viewYm) !== today.slice(0, 7)) return false;
  return ymdKey > today;
}

function normalizeDrugName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/（/g, '(')
    .replace(/）/g, ')')
    .replace(/\s+/g, '');
}

function toNumberOrNull(v) {
  if (v == null || v === '' || v === '—') return null;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

function formatQty(n) {
  if (n == null || !Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return n;
  const r = Math.round(n * 100) / 100;
  return r;
}

function appendSheet(wb, sheetName, rows) {
  if (!rows?.length) return;
  const ws = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
}

function pickLabCell(submission, itemName, sectionTitle, label) {
  const sections = submission?.sections || [];
  if (itemName) {
    for (const sec of sections) {
      for (const it of sec.items || []) {
        if (it?.name === itemName) {
          const v = it.value;
          if (v === null || v === undefined || v === '') return '';
          return String(v);
        }
      }
    }
  }
  const wantSec = String(sectionTitle || '').trim();
  const wantLab = String(label || '').trim();
  if (wantSec || wantLab) {
    for (const sec of sections) {
      const secTitle = String(sec?.title || '').trim();
      if (wantSec && secTitle && secTitle !== wantSec) continue;
      for (const it of sec.items || []) {
        const lab = String(it?.label || it?.html || it?.itemLabel || '').trim();
        const nm = String(it?.name || '').trim();
        if (wantLab && lab && lab !== wantLab && !nm.includes(wantLab)) continue;
        if (wantLab && !lab && nm && !nm.toLowerCase().includes(wantLab.toLowerCase())) {
          // name 形如 raw_NH3N，label 为氨氮时靠 itemName 路径已处理
          continue;
        }
        if (wantLab && lab === wantLab) {
          const v = it.value;
          if (v === null || v === undefined || v === '') return '';
          return String(v);
        }
      }
    }
  }
  return '';
}

function buildInfoRows(cityName, siteKey, siteDisplay, viewYm) {
  const { monthTitle } = parseYm(viewYm);
  return [
    ['湖南迪亚环境 — 正式四表版式导出'],
    ['城市', cityName],
    ['站点', siteDisplay],
    ['站点键', siteKey],
    ['查看月份', monthTitle],
    ['导出时间', new Date().toLocaleString('zh-CN')],
    [
      '说明',
      '工作表：药剂出库/入库记录、实验数据、处理水量日统计、水量结算。仅含已通过审核的数据。',
    ],
    [
      '水量说明',
      '系统尚未采集进/出水量与结算数据；「处理水量日统计」「水量结算」仅为与正式表一致的空模板骨架。',
    ],
    [
      '药剂说明',
      '出库/入库各一张表，版式相同。已通过审核的数量写入「生产出库/入库」；「研发」列暂留空；领料人为填报人；末行合计。',
    ],
  ];
}

/** 出库列名 → 入库列名（同一矩阵版式） */
function mapDrugColType(type, kind) {
  const t = String(type || '');
  if (kind !== 'in') return t || '生产出库';
  if (!t) return '生产入库';
  return t.replace(/出库/g, '入库');
}

/**
 * 药剂出入库登记表：日×药剂矩阵
 * @param {'out'|'in'} kind
 */
function buildDrugOfficialRows(data, viewYm, siteDisplay, kind = 'out') {
  const isIn = kind === 'in';
  const flowLabel = isIn ? '入库' : '出库';
  const prodType = isIn ? '生产入库' : '生产出库';
  const catalog = data?.drug_catalog;
  const drugsRaw = resolveDrugOutColumns(siteDisplay, catalog);
  const drugs = drugsRaw.map((d) => ({
    ...d,
    cols: (d.cols || ['生产出库']).map((c) => mapDrugColType(c, kind)),
  }));
  const { yyStr, mmStr, daysInMonth } = parseYm(viewYm);
  const ym = viewYm;

  if (!drugs.length) {
    return [[`暂无药剂目录，无法生成药剂${flowLabel}矩阵`]];
  }

  const flat = [];
  drugs.forEach((d) => {
    (d.cols || [prodType]).forEach((type) => {
      flat.push({ drugName: d.name, type });
    });
  });

  const titleRow = [
    '药剂出入库登记表',
    ...Array(Math.max(flat.length - 1, 0)).fill(''),
    '单位：kg/L',
  ];
  const nameRow = ['类别\n日期'];
  const typeRow = [''];
  drugs.forEach((d) => {
    const cols = d.cols || [prodType];
    cols.forEach((type, idx) => {
      nameRow.push(idx === 0 ? d.name : '');
      typeRow.push(type);
    });
  });
  nameRow.push('领料人');
  typeRow.push('');

  const byDay = new Map();
  const history = approvedOnly(data?.drug_inventory);
  for (const sub of history) {
    const ymd = fillYmd(sub.fillTime, sub.createdAt);
    if (!ymd || !ymd.startsWith(ym)) continue;
    if (isFutureDay(ymd, viewYm)) continue;
    const day = Number(ymd.slice(8, 10));
    if (!byDay.has(day)) byDay.set(day, { qty: new Map(), pickers: [] });
    const slot = byDay.get(day);
    let hasFlow = false;
    for (const r of sub.records || []) {
      const n = toNumberOrNull(isIn ? r?.inQuantity : r?.outQuantity);
      if (n == null) continue;
      hasFlow = true;
      const nk = normalizeDrugName(r.drugName);
      slot.qty.set(nk, (slot.qty.get(nk) || 0) + n);
    }
    if (hasFlow) {
      const un = String(sub.userName || '').trim();
      if (un) slot.pickers.push(un);
    }
  }

  const rows = [titleRow, nameRow, typeRow];
  const totals = flat.map(() => 0);

  for (let d = 1; d <= daysInMonth; d += 1) {
    const ymdKey = `${yyStr}-${mmStr}-${pad2(d)}`;
    const slot = byDay.get(d);
    if (isFutureDay(ymdKey, viewYm)) {
      rows.push([d, ...flat.map(() => ''), '']);
      continue;
    }
    const cells = [];
    for (let idx = 0; idx < flat.length; idx += 1) {
      const col = flat[idx];
      if (col.type !== prodType) {
        cells.push('');
        continue;
      }
      const q = slot?.qty?.get(normalizeDrugName(col.drugName));
      if (q == null) {
        cells.push('');
        continue;
      }
      totals[idx] += q;
      cells.push(formatQty(q));
    }
    const picker = slot?.pickers?.length
      ? [...new Set(slot.pickers)].join('、')
      : '';
    rows.push([d, ...cells, picker]);
  }

  const totalRow = [
    '合计',
    ...totals.map((t, idx) => {
      if (flat[idx].type !== prodType) return '';
      return formatQty(t);
    }),
    '',
  ];
  rows.push(totalRow);
  return rows;
}

/**
 * 实验数据 / 处理日报：两行表头 + 日行
 */
function buildLabOfficialRows(data, viewYm, siteDisplay) {
  const groups = resolveLabGroups(siteDisplay);
  const history = approvedOnly(data?.water_lab_history);
  const { yyStr, mmStr, daysInMonth, yy, mm } = parseYm(viewYm);
  const ym = viewYm;

  const flatCols = groups.flatMap((g) =>
    (g.cols || []).map((c) => ({
      groupTitle: g.title,
      label: c.label,
      name: c.name,
    }))
  );

  const title = `${yy}年${siteDisplay}处理日报表`;
  const groupRow = ['日期  项目'];
  const labelRow = [''];
  groups.forEach((g) => {
    const cols = g.cols || [];
    cols.forEach((c, idx) => {
      groupRow.push(idx === 0 ? g.title : '');
      labelRow.push(c.label || '');
    });
  });

  const byDay = new Map();
  for (const row of history) {
    const ymd = fillYmd(row.fillTime, row.createdAt);
    if (!ymd || ymd.slice(0, 7) !== ym) continue;
    if (!byDay.has(ymd)) byDay.set(ymd, []);
    byDay.get(ymd).push(row);
  }

  const rows = [[title], groupRow, labelRow];

  for (let d = 1; d <= daysInMonth; d += 1) {
    const ymdKey = `${yyStr}-${mmStr}-${pad2(d)}`;
    const dateLabel = `${mm}/${d}/${String(yy).slice(-2)}`;
    if (isFutureDay(ymdKey, viewYm)) {
      rows.push([dateLabel, ...flatCols.map(() => '')]);
      continue;
    }
    const subs = byDay.get(ymdKey) || [];
    if (!subs.length) {
      rows.push([dateLabel, ...flatCols.map(() => '')]);
      continue;
    }
    // 同日多条：取最新一条填数（与网页「按日展示」习惯一致）
    const sorted = [...subs].sort(
      (a, b) =>
        new Date(b.fillTime || b.createdAt || 0) -
        new Date(a.fillTime || a.createdAt || 0)
    );
    const sub = sorted[0];
    rows.push([
      dateLabel,
      ...flatCols.map((c) =>
        pickLabCell(sub, c.name, c.groupTitle, c.label) || ''
      ),
    ]);
  }
  return rows;
}

/**
 * 导出与正式四表一致的多工作表 Excel（单站 + 当前查看月份）
 */
export function downloadManualUploadExcel({ data, cityName, siteKey, viewYm }) {
  if (!data || !siteKey) return false;

  const siteDisplay = resolveSiteDisplayName(siteKey, cityName);
  const wb = XLSX.utils.book_new();

  appendSheet(wb, '说明', buildInfoRows(cityName, siteKey, siteDisplay, viewYm));
  appendSheet(wb, '药剂出库记录', buildDrugOfficialRows(data, viewYm, siteDisplay, 'out'));
  appendSheet(wb, '药剂入库记录', buildDrugOfficialRows(data, viewYm, siteDisplay, 'in'));
  appendSheet(wb, '实验数据', buildLabOfficialRows(data, viewYm, siteDisplay));
  appendSheet(wb, '处理水量日统计', buildWaterDailySkeletonRows(siteDisplay, viewYm));
  appendSheet(wb, '水量结算', buildWaterSettleSkeletonRows(siteDisplay, viewYm));

  const filename = `${siteDisplay}_正式表_${viewYm}.xlsx`;
  XLSX.writeFile(wb, filename);
  return true;
}
